import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../i18n/LanguageContext';
import { api } from '../../services/api';
import SettingsHeader from './SettingsHeader';
import AccountSettingsTab from './AccountSettingsTab';
import DocumentsTab from './DocumentsTab';
import OtherDocumentsTab from './OtherDocumentsTab';
import TeamMembersTab from './TeamMembersTab';
import WorkingHoursSettingsTab from './WorkingHoursSettingsTab';
import EditProfileModal from './EditProfileModal';
import SignedAgreementsTab from './SignedAgreementsTab';

type TabKey =
  | 'account'
  | 'working-hours'
  | 'trade-license'
  | 'documents'
  | 'team'
  | 'signed-agreements';

interface GarageInfo {
  name: string;
  email: string;
  phone: string;
  logoStorageKey: string | null;
}

export default function SettingsPage() {
  const { token, user } = useAuth();
  const { t } = useLanguage();
  // Spec 071 Flow E — owner-only tab, shown only while the signed-agreements switch is ON.
  const isOwner = user?.roles?.includes('GARAGE_OWNER') ?? false;
  const [signingEnabled, setSigningEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('account');
  const [garage, setGarage] = useState<GarageInfo | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditProfile, setShowEditProfile] = useState(false);

  useEffect(() => {
    if (!token) return;
    api.getMyGarage(token)
      .then(async (data) => {
        setGarage({
          name: data.name,
          email: data.email || '',
          phone: data.phone || '',
          logoStorageKey: null,
        });
        // Try to find logo from media array
        const logoMedia = data.media?.find((m: { mediaType?: string }) => m.mediaType === 'LOGO');
        if (logoMedia && 'storageKey' in logoMedia) {
          try {
            const dl = await api.getDownloadUrl((logoMedia as { storageKey: string }).storageKey, token);
            setLogoUrl(dl.downloadUrl);
          } catch { /* ignore */ }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!token || !isOwner) {
      setSigningEnabled(false);
      return;
    }
    let cancelled = false;
    api.getTermsSigningContext(token)
      .then((ctx) => { if (!cancelled) setSigningEnabled(ctx.enabled === true); })
      // Unknown switch state (or a backend without spec 071): keep the tab hidden.
      .catch(() => { if (!cancelled) setSigningEnabled(false); });
    return () => { cancelled = true; };
  }, [token, isOwner]);

  const showSignedAgreements = isOwner && signingEnabled;
  useEffect(() => {
    if (!showSignedAgreements && activeTab === 'signed-agreements') setActiveTab('account');
  }, [showSignedAgreements, activeTab]);

  if (loading) {
    return (
      <div className="settings-page">
        <div className="settings-loading">{t('common.loading')}</div>
      </div>
    );
  }

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'account', label: t('settings.accountSettings') },
    { key: 'working-hours', label: t('settings.workingHours.tab') || 'Working Hours' },
    { key: 'trade-license', label: t('settings.tradeLicenseTab') },
    { key: 'documents', label: t('settings.documentsTab') },
    { key: 'team', label: t('settings.teamMembers') },
  ];
  if (showSignedAgreements) {
    const label = t('settings.signedAgreements.tab');
    tabs.push({
      key: 'signed-agreements',
      label: label && label !== 'settings.signedAgreements.tab' ? label : 'Signed agreements',
    });
  }

  return (
    <div className="settings-page">
      <div className="settings-page-header">
        <h1 className="settings-title">{t('settings.title')}</h1>
        <p className="settings-subtitle">{t('settings.subtitle')}</p>
      </div>

      <SettingsHeader
        garageName={garage?.name || ''}
        email={garage?.email || ''}
        phone={garage?.phone || ''}
        logoUrl={logoUrl}
        onEditProfile={() => setShowEditProfile(true)}
      />

      <div className="settings-tabs">
        {tabs.map(tab => (
          <button
            key={tab.key}
            className={`settings-tab ${activeTab === tab.key ? 'settings-tab-active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'account' && <AccountSettingsTab />}
      {activeTab === 'working-hours' && <WorkingHoursSettingsTab />}
      {activeTab === 'trade-license' && <DocumentsTab />}
      {activeTab === 'documents' && <OtherDocumentsTab />}
      {activeTab === 'team' && <TeamMembersTab />}
      {activeTab === 'signed-agreements' && showSignedAgreements && <SignedAgreementsTab />}

      {showEditProfile && garage && (
        <EditProfileModal
          currentEmail={garage.email}
          currentPhone={garage.phone}
          onClose={() => setShowEditProfile(false)}
          onSaved={(email, phone) => {
            setGarage(prev => prev ? { ...prev, email, phone } : prev);
            setShowEditProfile(false);
          }}
        />
      )}
    </div>
  );
}
